import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../api/http";
import "../../styles/register/Register.css";

function Register() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: "",
    username: "",
    password: "",
    confirm_password: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const toast = (message, type = "error") => {
    const el = document.createElement("div");
    el.className = `sr-register__toast sr-register__toast--${type}`;
    el.textContent = message;
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 3200);
  };

  const validation = useMemo(() => {
    const email = formData.email.trim();
    const username = formData.username.trim();
    const password = formData.password;
    const confirm = formData.confirm_password;

    if (!email) {
      return { ok: false, message: "Introduceți adresa de email." };
    }

    if (!/^\S+@\S+\.\S+$/.test(email)) {
      return { ok: false, message: "Adresa de email nu este validă." };
    }

    if (!username) {
      return { ok: false, message: "Introduceți un nume de utilizator." };
    }

    if (username.length < 3) {
      return { ok: false, message: "Numele de utilizator trebuie să conțină cel puțin 3 caractere." };
    }

    if (!password) {
      return { ok: false, message: "Introduceți parola." };
    }

    if (password.length < 8) {
      return { ok: false, message: "Parola trebuie să conțină cel puțin 8 caractere." };
    }

    const hasLetter = /[a-zA-Z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSymbol = /[^a-zA-Z0-9]/.test(password);

    if (!hasLetter || !hasNumber || !hasSymbol) {
      return {
        ok: false,
        message: "Parola trebuie să conțină litere, cifre și simboluri.",
      };
    }

    if (!confirm) {
      return { ok: false, message: "Confirmați parola." };
    }

    if (password !== confirm) {
      return { ok: false, message: "Parolele nu coincid." };
    }

    return { ok: true };
  }, [formData]);

  const parseError = (err) => {
    const data = err?.response?.data;
    if (!data) return "Înregistrarea a eșuat. Încercați din nou.";

    if (typeof data === "string") return data;
    if (typeof data.detail === "string") return data.detail;

    if (data.password?.[0]) {
      const msg = data.password[0].toLowerCase();
      if (msg.includes("symbol") || msg.includes("letter") || msg.includes("number")) {
        return "Parola trebuie să conțină litere, cifre și simboluri.";
      }
      if (msg.includes("8")) {
        return "Parola trebuie să conțină cel puțin 8 caractere.";
      }
    }

    if (data.confirm_password) {
      return "Confirmați parola.";
    }

    if (data.email?.[0]) {
      return data.email[0].includes("exist")
        ? "Această adresă de email este deja înregistrată."
        : "Adresa de email nu este validă.";
    }

    if (data.username?.[0]) {
      return "Acest nume de utilizator este deja folosit.";
    }

    return "Înregistrarea a eșuat. Verificați datele introduse.";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validation.ok) {
      toast(validation.message, "error");
      return;
    }

    try {
      await api.post("/authen/register/", {
        email: formData.email.trim(),
        username: formData.username.trim(),
        password: formData.password,
        confirm_password: formData.confirm_password,
      });

      toast("Contul a fost creat cu succes. Redirecționare către autentificare…", "success");
      setTimeout(() => navigate("/login", { replace: true }), 800);
    } catch (err) {
      toast(parseError(err), "error");
    }
  };

  return (
    <div className="sr-register">
      <div className="sr-register__card">
        <div className="sr-register__head">
          <h2 className="sr-register__title">Înregistrare</h2>
          <p className="sr-register__subtitle">Creează un cont în câteva secunde.</p>
        </div>

        <form onSubmit={handleSubmit} className="sr-register__form">
          <div className="sr-register__field">
            <label className="sr-register__label">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="sr-register__input"
            />
          </div>

          <div className="sr-register__field">
            <label className="sr-register__label">Nume utilizator</label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              className="sr-register__input"
            />
          </div>

          <div className="sr-register__field">
            <label className="sr-register__label">Parolă</label>
            <div className="sr-register__passwordWrap">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="sr-register__input"
              />
              <button
                type="button"
                className={`sr-register__eye ${showPassword ? "is-active" : ""}`}
                onClick={() => setShowPassword((v) => !v)}
              >
                👁
              </button>
            </div>
          </div>

          <div className="sr-register__field">
            <label className="sr-register__label">Confirmă parola</label>
            <div className="sr-register__passwordWrap">
              <input
                type={showConfirm ? "text" : "password"}
                name="confirm_password"
                value={formData.confirm_password}
                onChange={handleChange}
                className="sr-register__input"
              />
              <button
                type="button"
                className={`sr-register__eye ${showConfirm ? "is-active" : ""}`}
                onClick={() => setShowConfirm((v) => !v)}
              >
                👁
              </button>
            </div>
          </div>

          <button type="submit" className="sr-register__submit">
            Creează cont
          </button>
        </form>

        <div className="sr-register__footer">
          <span className="sr-register__footerText">Ai deja cont?</span>
          <Link to="/login" className="sr-register__link sr-register__link--strong">
            Login
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Register;